import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import {
  Container,
  Dialog,
  DialogContentText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import conf from "../../conf";
import Spinner from "../utils/Spinner";
import SectionHeader from "../utils/SectionHeader";
import { IdpConfig, useConfgOrganizationEditIdp } from "../../hooks/confg/base";

type ProviderEditViewProps = {
  idp: IdpConfig;
  open: boolean;
  onClose: () => void;
};
const ProviderEditView = (props: ProviderEditViewProps) => {
  const { open, onClose, idp } = props;

  const [locked, setLocked] = useState(false);

  const [tenantId, setTenantId] = useState(idp.tenantId);
  const [clientId, setClientId] = useState(idp.clientId);
  const [authority, setAuthority] = useState(idp.authority);
  const [clientSecret, setClientSecret] = useState(idp.clientSecret);

  const edit = useConfgOrganizationEditIdp();

  const handleEdit = () => {
    edit.mutate({
      name: idp.name,
      authority,
      tenantId,
      clientId,
      clientSecret,
    });
  };

  const resetForm = () => {
    setLocked(false);
    setTenantId(idp.tenantId);
    setClientId(idp.clientId);
    setAuthority(idp.authority);
    setClientSecret(idp.clientSecret);
  };

  useEffect(() => {
    setTenantId(idp.tenantId);
    setClientId(idp.clientId);
    setAuthority(idp.authority);
    setClientSecret(idp.clientSecret);
  }, [idp]);

  useEffect(() => {
    if (edit.isSuccess) {
      resetForm();
      onClose();
    }
  }, [edit.isSuccess]);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key="idp-add-header"
          title="Edit provider"
          icon={<EditIcon />}
          buttons={[
            <Button
              key={"idp-add-reset"}
              color="inherit"
              size="small"
              disabled={locked}
              onClick={resetForm}
            >
              Reset
            </Button>,

            <Button
              key={"idp-add-close"}
              size="small"
              color="inherit"
              onClick={onClose}
            >
              <CloseIcon />
            </Button>,
          ]}
        />

        <Box component="form" noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DialogContentText variant="caption">
                To edit an identity provider, please make changes then submit.
                Changes will not take effect until saved & submitted.
              </DialogContentText>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption">{idp.name}</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-idp-authority"
                name="organization-idp-authority"
                label="Authority"
                value={authority}
                disabled={locked}
                onChange={(e: any) => setAuthority(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-idp-tenant-id"
                name="organization-idp-tenant-id"
                label="Tenant ID"
                value={tenantId}
                disabled={locked}
                onChange={(e: any) => setTenantId(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-idp-client-id"
                name="organization-idp-client-id"
                label="Client ID"
                value={clientId}
                disabled={locked}
                onChange={(e: any) => setClientId(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="organization-idp-client-secret"
                name="organization-idp-client-secret"
                type="password"
                label="Client Secret"
                value={clientSecret}
                disabled={locked}
                onChange={(e: any) => setClientSecret(e.target.value)}
              />
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <Button
              variant={locked ? "outlined" : "contained"}
              onClick={() => setLocked(!locked)}
              sx={{ mt: 3, mb: 2, mr: 2 }}
            >
              {locked ? "Cancel" : "Save"}
            </Button>

            <Button
              variant="contained"
              disabled={!locked}
              onClick={handleEdit}
              sx={{ mt: 3, mb: 2 }}
            >
              {edit.isPending ? <Spinner sx={{ mx: 2.5 }} /> : "Submit"}
            </Button>
          </Stack>
        </Box>
      </Container>
    </Dialog>
  );
};

const ProviderEdit = ({
  idp,
  disabled = false,
}: {
  idp: IdpConfig;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <div>
      <Button
        size="small"
        variant="contained"
        disabled={disabled}
        onClick={handleOpen}
      >
        Edit
      </Button>

      <ProviderEditView idp={idp} open={open} onClose={handleClose} />
    </div>
  );
};

export default ProviderEdit;
